import { Link } from "react-router-dom";
import { Muted, Small } from "@/components/ui/typography";
import { EnvelopeIcon, ArrowSquareOutIcon, CaretRightIcon, FacebookLogoIcon, InstagramLogoIcon, MapPinIcon, PhoneIcon } from "@phosphor-icons/react";
import { VENUE, VENUE_MAP_URL } from "@/lib/venue";

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    // A háttér a fejlécé (`bg-background`, a fejléc átlátszó a lapon): a kettő egy keretet
    // ad. A zöld fényfoltok maradnak díszítésnek, és erre az alapra vannak hangolva.
    <footer className="relative bg-background border-t border-border mt-auto overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
      </div>

      {/* Alul legalább annyi hely, amennyit egy képernyő alján lebegő sáv elfoglal
          (`--floating-bar-space`, a `PillTabNav floating` állítja): görgetés végén se takarjon */}
      <div className="relative container mx-auto px-6 pt-12 pb-[max(2rem,var(--floating-bar-space,0px))]">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {/* Brand & Description */}
          <div className="lg:col-span-1 space-y-5">
            <Link to="/" className="inline-block group">
              <img
                src="/arizona-bowling-logo.png"
                alt="Arizona Bowling"
                className="h-12 group-hover:scale-105 transition-transform dark:hidden"
              />
              <img
                src="/arizona-bowling-logo-sotet.png"
                alt="Arizona Bowling"
                className="hidden h-12 group-hover:scale-105 transition-transform dark:block"
              />
            </Link>
            <Muted>
              Foglalj időpontot az Arizona Bowling pályáira! Válaszd ki a megfelelő dátumot és időpontot, add meg a vendégek számát, és már indulhat is a szórakozás!
            </Muted>
            {/* Social icons */}
            <div className="flex gap-3 pt-2">
              <a
                href={VENUE.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-background hover:bg-primary border border-border hover:border-primary shadow-xs hover:shadow-md hover:shadow-primary/20 transition-all duration-300"
                aria-label="Facebook"
              >
                <FacebookLogoIcon className="h-5 w-5 text-muted-foreground group-hover:text-white transition-colors" />
              </a>
              <a
                href={VENUE.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-background hover:bg-primary border border-border hover:border-primary shadow-xs hover:shadow-md hover:shadow-primary/20 transition-all duration-300"
                aria-label="Instagram"
              >
                <InstagramLogoIcon className="h-5 w-5 text-muted-foreground group-hover:text-white transition-colors" />
              </a>
              <a
                href={`mailto:${VENUE.email}`}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-background hover:bg-primary border border-border hover:border-primary shadow-xs hover:shadow-md hover:shadow-primary/20 transition-all duration-300"
                aria-label="Email"
              >
                <EnvelopeIcon className="h-5 w-5 text-muted-foreground group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-5">
            <Small render={<h3 />}>
              Navigáció
            </Small>
            <ul className="space-y-3">
              {[
                { to: "/", label: "Főoldal" },
                { to: "/booking", label: "Foglalás" },
                { to: "/my-bookings", label: "Foglalásaim" },
                { to: "/profile", label: "Profilom" },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="group flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <CaretRightIcon className="w-4 h-4 mr-1 text-muted-foreground/70 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div className="space-y-5">
            <Small render={<h3 />}>
              Jogi információk
            </Small>
            <ul className="space-y-3">
              {[
                { href: "https://www.arizona-bowling.com/aszf/", label: "ÁSZF" },
                { href: "https://www.arizona-bowling.com/adatvedelmi-tajekoztato/", label: "Adatvédelem" },
                { href: "https://www.arizona-bowling.com/hazirend/", label: "Bowling házirend" },
                { href: "https://www.arizona-bowling.com/impresszum/", label: "Impresszum" },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ArrowSquareOutIcon className="w-3.5 h-3.5 mr-2 text-muted-foreground/70 group-hover:text-primary transition-colors" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-5">
            <Small render={<h3 />}>
              Elérhetőségek
            </Small>
            <ul className="space-y-4">
              <li>
                <a
                  href={VENUE_MAP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-3 text-sm"
                >
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-background border border-border shadow-xs flex items-center justify-center group-hover:bg-primary/10 group-hover:border-primary/30 transition-all">
                    <MapPinIcon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="pt-1">
                    <span className="text-muted-foreground group-hover:text-foreground transition-colors">{VENUE.address}</span>
                  </div>
                </a>
              </li>
              {VENUE.phones.map((phone) => (
                <li key={phone.tel}>
                  <a href={`tel:${phone.tel}`} className="group flex items-start gap-3 text-sm">
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-background border border-border shadow-xs flex items-center justify-center group-hover:bg-primary/10 group-hover:border-primary/30 transition-all">
                      <PhoneIcon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div className="pt-0.5">
                      <span className="text-muted-foreground group-hover:text-foreground transition-colors block">{phone.display}</span>
                      <span className="text-xs text-muted-foreground/70">{phone.label}</span>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-border">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground/70">
              © {currentYear} Arizona Bowling. Minden jog fenntartva.
            </p>
            <p className="text-xs text-muted-foreground/70">
              Készítette:{" "}
              <a
                href="https://csonka-alex-bio.web.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Csonka Alex
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
